
import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import UsersTab from "./UsersTab";
import ClassesTab from "./ClassesTab";
import BookingsTab from "./BookingsTab";
import SubscriptionsTab from "./SubscriptionsTab";
import GiftCardsTab from "./GiftCardsTab";
import { useAdminDashboard } from "@/contexts/AdminDashboardContext";
import { useAdminQueries } from "@/hooks/useAdminQueries";

interface AdminDashboardContentProps {
  defaultTab?: string;
}

const AdminDashboardContent: React.FC<AdminDashboardContentProps> = ({ 
  defaultTab = "bookings" 
}) => {
  const { 
    setIsAddGiftCardOpen, 
    setIsScheduleDialogOpen, 
    setIsAddSubscriptionOpen,
    handleUpdateSubscriptionStatus,
    handleUpdateSubscriptionPaymentStatus,
    handleEditSubscription,
    handleDeleteSubscription,
    handleDeleteBooking,
    handleUpdatePaymentStatus,
    handleRescheduleBooking,
    handleDeleteClass
  } = useAdminDashboard();

  const { 
    classes, classesLoading, classesError, 
    bookings, bookingsLoading, bookingsError, 
    users, usersLoading, usersError, userFilter, setUserFilter,
    subscriptions, subscriptionsLoading, subscriptionsError, subscriptionFilter, setSubscriptionFilter,
    giftCards, giftCardsLoading, giftCardsError, giftCardFilter, setGiftCardFilter
  } = useAdminQueries();

  return (
    <Tabs defaultValue={defaultTab} className="mt-6">
      <TabsList className="mb-4">
        <TabsTrigger value="users">Users</TabsTrigger>
        <TabsTrigger value="classes">Classes</TabsTrigger>
        <TabsTrigger value="bookings">Bookings</TabsTrigger>
        <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
        <TabsTrigger value="gift-cards">Gift Cards</TabsTrigger>
      </TabsList>
      
      <TabsContent value="users">
        <UsersTab 
          users={users} 
          userFilter={userFilter}
          setUserFilter={setUserFilter}
          isLoading={usersLoading} 
          error={usersError} 
        />
      </TabsContent>
      
      <TabsContent value="classes">
        <ClassesTab 
          classes={classes}
          isLoading={classesLoading}
          error={classesError}
          onDeleteClass={handleDeleteClass}
        />
      </TabsContent>
      
      <TabsContent value="bookings">
        <BookingsTab 
          bookings={bookings}
          isLoading={bookingsLoading}
          error={bookingsError}
          onDeleteBooking={handleDeleteBooking}
          onUpdatePaymentStatus={handleUpdatePaymentStatus}
          onRescheduleBooking={handleRescheduleBooking}
        />
      </TabsContent>
      
      <TabsContent value="subscriptions">
        <SubscriptionsTab 
          subscriptions={subscriptions}
          filteredSubscriptions={subscriptions?.filter(sub => {
            if (!subscriptionFilter) return true;
            const lowerFilter = subscriptionFilter.toLowerCase();
            return (
              (sub.userName && sub.userName.toLowerCase().includes(lowerFilter)) || 
              (sub.userEmail && sub.userEmail.toLowerCase().includes(lowerFilter))
            );
          })}
          subscriptionFilter={subscriptionFilter}
          setSubscriptionFilter={setSubscriptionFilter}
          isLoading={subscriptionsLoading}
          error={subscriptionsError}
          onAddSubscription={() => setIsAddSubscriptionOpen(true)}
          onUpdateStatus={handleUpdateSubscriptionStatus}
          onUpdatePaymentStatus={handleUpdateSubscriptionPaymentStatus}
          onEditSubscription={handleEditSubscription}
          onDeleteSubscription={handleDeleteSubscription}
        />
      </TabsContent>
      
      <TabsContent value="gift-cards">
        <GiftCardsTab 
          giftCards={giftCards}
          filteredGiftCards={giftCards?.filter(card => {
            if (!giftCardFilter) return true;
            const lowerFilter = giftCardFilter.toLowerCase();
            return (
              (card.firstName && card.firstName.toLowerCase().includes(lowerFilter)) || 
              (card.lastName && card.lastName.toLowerCase().includes(lowerFilter))
            );
          })}
          giftCardFilter={giftCardFilter}
          setGiftCardFilter={setGiftCardFilter}
          isLoading={giftCardsLoading}
          error={giftCardsError}
          onAddGiftCard={() => setIsAddGiftCardOpen(true)}
        />
      </TabsContent>
    </Tabs>
  );
};

export default AdminDashboardContent;
